// Consultation/ConsultationConfigPicker.tsx
import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from "Redux/store";
import {Checkbox, FormControlLabel} from '@mui/material';
import RadioLine from 'Core/components/elements/RadioLine';
import {useTheme} from "Theme/ThemeContext";
import {FC, FR} from "WideLayout/Layouts";
import {Message} from "Core/components/Message";
import {IPsychologist} from "../../Types/psychology/psychologist";
import {IConsultationConfig} from "../../Types/psychology/consultation";
import {getConsultationTypeLabel} from "Locale/ruMap";

interface ConsultationConfigPickerProps {
    defaultConsultationConfigId?: number;
    cls?: string;
    psychologist?: IPsychologist;
    control: any;
    setValue: any;
    watch: any;
    defaultSupervision?: boolean;
    defaultIsOffline?: boolean;
    hideSupervision?: boolean;
    hideOffline?: boolean;
}

const ConsultationConfigPicker: React.FC<ConsultationConfigPickerProps> = (
    {
        defaultConsultationConfigId,
        psychologist = undefined,
        cls,
        control,
        setValue,
        watch,
        defaultSupervision = false,
        defaultIsOffline = false,
        hideSupervision = false,
        hideOffline = false,
    }) => {
    const {plt} = useTheme();
    const {consultation_configs} = useSelector((state: RootState) => state.psychologyParams);
    const consultation_config = watch('consultation_config', null);

    // Если чекбокс скрыт, значит состояние фиксировано значением по умолчанию.
    const [isSupervision, setIsSupervision] = useState<boolean>(defaultSupervision);
    const [isOffline, setIsOffline] = useState<boolean>(defaultIsOffline);

    const [filteredConfigs, setFilteredConfigs] = useState<IConsultationConfig[]>([]);
    const [availableTypes, setAvailableTypes] = useState<string[]>([]);
    const [availableDurations, setAvailableDurations] = useState<number[]>([]);
    const [selectedType, setSelectedType] = useState<string>('');
    const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
    const [previousConfig, setPreviousConfig] = useState<IConsultationConfig | null>(null);
    const [messageShown, setMessageShown] = useState<boolean>(false);
    const [errorShown, setErrorShown] = useState<boolean>(false);

    const isProgrammaticChange = useRef<boolean>(false);
    const defaultConfigRef = useRef<IConsultationConfig | null>(null);

    // База конфигов
    const baseConfigs = React.useMemo<IConsultationConfig[]>(() => {
        const src: IConsultationConfig[] =
            (psychologist && psychologist.consultation_configs.length > 0)
                ? psychologist.consultation_configs
                : consultation_configs;
        return src;
    }, [psychologist, consultation_configs]);

    // Функция для фильтрации конфигов по текущему состоянию isSupervision и isOffline,
    // а также по hideSupervision и hideOffline. Если чекбокс скрыт, то берем за основу default значение.
    const filterConfigs = (configs: IConsultationConfig[]): IConsultationConfig[] => {
        let result = [...configs];

        const supervisionValue = hideSupervision ? defaultSupervision : isSupervision;
        result = result.filter(cfg => cfg.is_supervision === supervisionValue);

        const offlineValue = hideOffline ? defaultIsOffline : isOffline;
        result = result.filter(cfg => cfg.is_online === !offlineValue);

        return result;
    };

    // 1. Исходные конфиги
    useEffect(() => {
        const fc = filterConfigs(baseConfigs);
        setFilteredConfigs(fc);
    }, [baseConfigs, hideSupervision, hideOffline, isSupervision, isOffline, defaultSupervision, defaultIsOffline]);

    // 2. Пересчет доступных типов
    useEffect(() => {
        const matchedTypes = new Map<string, Set<number>>();
        filteredConfigs.forEach(config => {
            const setForType = matchedTypes.get(config.type.name) || new Set<number>();
            setForType.add(config.duration.value);
            matchedTypes.set(config.type.name, setForType);
        });

        const filteredTypes = Array.from(matchedTypes.keys()).filter(typeName => matchedTypes.get(typeName)!.size > 0);
        setAvailableTypes(filteredTypes);

        if (selectedType && !filteredTypes.includes(selectedType)) {
            if (filteredTypes.length > 0) {
                setSelectedType(filteredTypes[0]);
            } else {
                setSelectedType('');
                setSelectedDuration(null);
                setValue('consultation_config', null);
            }
        }
    }, [filteredConfigs, selectedType, setValue]);

    // 3. Если тип не выбран
    useEffect(() => {
        if (!selectedType && availableTypes.length > 0) {
            setSelectedType(availableTypes[0]);
        }
    }, [availableTypes, selectedType]);

    // 4. Обработка defaultConsultationConfigId
    useEffect(() => {
        if (defaultConsultationConfigId && !consultation_config) {
            let defaultConfig: IConsultationConfig | undefined;

            defaultConfig = baseConfigs.find(config => config.id === defaultConsultationConfigId);

            if (!defaultConfig) {
                if (!errorShown) {
                    setErrorShown(true);
                    Message.error('Дефолтная конфигурация не найдена.');
                }
                return;
            }

            defaultConfigRef.current = defaultConfig;
            if (!hideSupervision) setIsSupervision(defaultConfig.is_supervision);
            if (!hideOffline) setIsOffline(!defaultConfig.is_online);

            setSelectedType(defaultConfig.type.name);
            setSelectedDuration(defaultConfig.duration.value);
            setValue('consultation_config', defaultConfig);
            setPreviousConfig(defaultConfig);
        }
    }, [defaultConsultationConfigId, consultation_config, baseConfigs, setValue, errorShown, hideSupervision, hideOffline]);

    // 5. Обновляем длительности
    useEffect(() => {
        if (!selectedType) {
            setAvailableDurations([]);
            setSelectedDuration(null);
            setValue('consultation_config', null);
            return;
        }

        const durationsSet = new Set<number>();
        filteredConfigs
            .filter(config => config.type.name === selectedType)
            .forEach(config => durationsSet.add(config.duration.value));
        const durationsArray = Array.from(durationsSet);
        setAvailableDurations(durationsArray);

        if (durationsSet.size > 0) {
            if (selectedDuration === null || !durationsSet.has(selectedDuration)) {
                if (defaultConfigRef.current && defaultConfigRef.current.type.name === selectedType) {
                    const defaultDuration = defaultConfigRef.current.duration.value;
                    if (durationsSet.has(defaultDuration)) {
                        isProgrammaticChange.current = true;
                        setSelectedDuration(defaultDuration);
                        const newConfig = filteredConfigs.find(config =>
                            config.type.name === selectedType &&
                            config.duration.value === defaultDuration
                        );
                        if (newConfig) {
                            setValue('consultation_config', newConfig);
                            setPreviousConfig(newConfig);
                            setMessageShown(false);
                        }
                        return;
                    }
                }

                const firstDuration = durationsArray[0];
                isProgrammaticChange.current = true;
                setSelectedDuration(firstDuration);
                const newConfig = filteredConfigs.find(config =>
                    config.type.name === selectedType &&
                    config.duration.value === firstDuration
                );
                if (newConfig) {
                    setValue('consultation_config', newConfig);
                    setPreviousConfig(newConfig);
                    setMessageShown(false);
                }
            }
        } else {
            setSelectedDuration(null);
            setValue('consultation_config', null);
            if (!messageShown && selectedType) {
                Message.info('Нет доступных длительностей для выбранного типа консультации.');
                setMessageShown(true);
            }
        }
    }, [selectedType, filteredConfigs, selectedDuration, setValue, messageShown]);

    // 6. Обновляем конфиг по длительности
    useEffect(() => {
        if (isProgrammaticChange.current) {
            isProgrammaticChange.current = false;
            return;
        }

        if (!selectedType || selectedDuration === null) {
            setValue('consultation_config', null);
            return;
        }

        const matchingConfig = filteredConfigs.find(config =>
            config.type.name === selectedType &&
            config.duration.value === selectedDuration
        );

        if (matchingConfig) {
            setValue('consultation_config', matchingConfig);
            setPreviousConfig(matchingConfig);
            setMessageShown(false);
        } else {
            if (!messageShown) {
                Message.info(`К сожалению, психолог не проводит ${configDescription(isOffline, isSupervision, selectedType, selectedDuration)}.`);
                if (previousConfig) {
                    isProgrammaticChange.current = true;
                    if (!hideSupervision) setIsSupervision(previousConfig.is_supervision);
                    if (!hideOffline) setIsOffline(!previousConfig.is_online);
                    setSelectedType(previousConfig.type.name);
                    setSelectedDuration(previousConfig.duration.value);
                    setValue('consultation_config', previousConfig);
                }
                setMessageShown(true);
            }
        }
    }, [selectedDuration, selectedType, filteredConfigs, setValue, previousConfig, messageShown, isSupervision, isOffline, hideSupervision, hideOffline]);

    const handleSupervisionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsSupervision(event.target.checked);
    };

    const handleOfflineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsOffline(event.target.checked);
    };

    const handleTypeChange = (value: string | number) => {
        setSelectedType(String(value));
        if (defaultConfigRef.current && defaultConfigRef.current.type.name === String(value)) {
            if (filteredConfigs.some(config => config.id === defaultConfigRef.current?.id)) {
                isProgrammaticChange.current = true;
                if (!hideSupervision) setIsSupervision(defaultConfigRef.current.is_supervision);
                if (!hideOffline) setIsOffline(!defaultConfigRef.current.is_online);
                setSelectedDuration(defaultConfigRef.current.duration.value);
                setValue('consultation_config', defaultConfigRef.current);
            }
        }
    };

    const handleDurationChange = (value: string | number) => {
        setSelectedDuration(Number(value));
    };

    // Доступность чекбоксов считаем по общей базе
    const supervisionDisabled = hideSupervision ? true : (baseConfigs ? !baseConfigs.some(config => config.is_supervision) : false);
    const offlineDisabled = hideOffline ? true : (baseConfigs ? !baseConfigs.some(config => !config.is_online) : false);

    useEffect(() => {
        if (!hideSupervision && supervisionDisabled && isSupervision) {
            setIsSupervision(false);
        }
        if (!hideOffline && offlineDisabled && isOffline) {
            setIsOffline(false);
        }
    }, [supervisionDisabled, offlineDisabled, isSupervision, isOffline, hideSupervision, hideOffline]);

    if (filteredConfigs.length === 0) {
        return (
            <FR cls={cls} style={{padding: '1em', textAlign: 'center'}}>
                <span>Нет доступных конфигураций.</span>
            </FR>
        );
    }

    const configDescription = (isOffline: boolean, isSupervision: boolean, type: string, duration: number) => {
        const onlineOffline = isOffline ? 'оффлайн' : 'онлайн';
        const supervision = isSupervision ? 'супервизию' : 'консультацию';
        const typeLabel = getConsultationTypeLabel(type);
        return `${onlineOffline} ${typeLabel.toLowerCase()} ${supervision} ${duration} минут`;
    };

    return (
        <FC cls={cls}>
            <FR>
                {!hideSupervision && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isSupervision}
                                onChange={handleSupervisionChange}
                                disabled={supervisionDisabled}
                            />
                        }
                        label="Супервизия"
                    />
                )}
                {!hideOffline && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isOffline}
                                onChange={handleOfflineChange}
                                disabled={offlineDisabled}
                            />
                        }
                        label="Оффлайн"
                    />
                )}
            </FR>

            <FR wrap g={2}>
                <div className={'fcc gap-1'}>
                    {availableTypes.length > 0 ? (
                        <RadioLine
                            options={availableTypes.map(typeName => ({
                                value: typeName,
                                label: getConsultationTypeLabel(typeName),
                            }))}
                            selectedValue={selectedType}
                            onChange={handleTypeChange}
                            className="consultation-type-radio"
                            itemClass="consultation-type-item"
                        />
                    ) : (
                        <span>Нет доступных типов консультаций</span>
                    )}
                </div>

                <div className={'frsc gap-1'}>
                    {availableDurations.length > 0 ? (
                        <>
                            <RadioLine
                                options={availableDurations.map(durationValue => ({
                                    value: durationValue.toString(),
                                    label: durationValue.toString(),
                                }))}
                                selectedValue={selectedDuration ? selectedDuration.toString() : null}
                                onChange={handleDurationChange}
                                className="duration-radio"
                                itemClass="duration-item"
                            />
                            <span style={{color: plt.text.primary70}}>минут</span>
                        </>
                    ) : (
                        <span>Нет доступных длительностей</span>
                    )}
                </div>
            </FR>
        </FC>
    );
};

export default ConsultationConfigPicker;
